import React, { useState, useEffect } from 'react'
import { 
  Trash2, RefreshCw, AlertTriangle, 
  Clock, Calendar, ArrowUpRight
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../hooks/useToast'
import { 
  getTrashItems, 
  restoreItem, 
  permanentlyDeleteItem, 
  emptyTrash 
} from '../services/fileService'
import { formatFileSize } from '../utils/fileHelpers'
import TrashGridView from '../components/TrashGridView'

interface TrashItem {
  id: string
  name: string
  type: 'file' | 'folder'
  size?: number
  mimeType?: string
  modifiedAt: string
  path: string
  deletedAt?: string
}

const TrashPage: React.FC = () => {
  const { user } = useAuth()
  const { showToast } = useToast()
  const [trashItems, setTrashItems] = useState<TrashItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [isConfirmingEmpty, setIsConfirmingEmpty] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    return localStorage.getItem('trashViewMode') as 'grid' | 'list' || 'list'
  })
  
  useEffect(() => {
    loadTrashItems()
  }, [])
  
  useEffect(() => {
    localStorage.setItem('trashViewMode', viewMode)
  }, [viewMode])
  
  const loadTrashItems = async () => {
    try {
      setLoading(true)
      const items = await getTrashItems()
      setTrashItems(items)
    } catch (error) {
      console.error('Failed to load trash items:', error)
      showToast('Failed to load trash items', 'error')
    } finally {
      setLoading(false)
    }
  }
  
  const handleRestore = async (itemId: string) => {
    try {
      await restoreItem(itemId)
      showToast('Item restored successfully', 'success')
      loadTrashItems()
      
      // Remove from selection if selected
      if (selectedItems.includes(itemId)) {
        setSelectedItems(prev => prev.filter(id => id !== itemId))
      }
    } catch (error) {
      console.error('Failed to restore item:', error)
      showToast('Failed to restore item', 'error')
    }
  }
  
  const handlePermanentDelete = async (itemId: string) => {
    try {
      await permanentlyDeleteItem(itemId)
      showToast('Item permanently deleted', 'success')
      loadTrashItems()
      
      // Remove from selection if selected
      if (selectedItems.includes(itemId)) {
        setSelectedItems(prev => prev.filter(id => id !== itemId))
      }
    } catch (error) {
      console.error('Failed to delete item:', error)
      showToast('Failed to delete item', 'error')
    }
  }
  
  const handleEmptyTrash = async () => {
    try {
      await emptyTrash()
      showToast('Trash emptied successfully', 'success')
      setTrashItems([])
      setSelectedItems([])
      setIsConfirmingEmpty(false)
    } catch (error) {
      console.error('Failed to empty trash:', error)
      showToast('Failed to empty trash', 'error')
    }
  }
  
  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    )
  }
  
  const handleSelectAll = () => {
    if (selectedItems.length === trashItems.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(trashItems.map(item => item.id))
    }
  }
  
  const handleRestoreSelected = async () => {
    if (selectedItems.length === 0) return
    
    try {
      for (const itemId of selectedItems) {
        await restoreItem(itemId)
      }
      showToast(`${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} restored`, 'success')
      setSelectedItems([])
      loadTrashItems()
    } catch (error) {
      console.error('Failed to restore items:', error)
      showToast('Failed to restore items', 'error')
    }
  }
  
  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return
    
    try {
      for (const itemId of selectedItems) {
        await permanentlyDeleteItem(itemId)
      }
      showToast(`${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} permanently deleted`, 'success')
      setSelectedItems([])
      loadTrashItems()
    } catch (error) {
      console.error('Failed to delete items:', error)
      showToast('Failed to delete items', 'error')
    }
  }
  
  const getDaysRemaining = (deletedAt?: string): number => {
    if (!deletedAt) return 0
    
    const deletedDate = new Date(deletedAt)
    const expiryDate = new Date(deletedDate)
    expiryDate.setDate(expiryDate.getDate() + 30)
    
    const now = new Date()
    const diffTime = expiryDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return Math.max(0, diffDays)
  }
  
  return (
    <div className="h-full overflow-auto p-6 pb-16">
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">Trash</h1>
            <p className="text-[var(--color-accent)]">
              Items in trash will be permanently deleted after 30 days
            </p>
          </div>
          
          <div className="flex items-center space-x-2">
            {selectedItems.length > 0 ? (
              <>
                <button
                  className="btn btn-primary flex items-center"
                  onClick={handleRestoreSelected}
                >
                  <RefreshCw size={18} className="mr-2" />
                  Restore ({selectedItems.length})
                </button>
                <button
                  className="btn bg-red-500 text-white hover:bg-red-600 flex items-center"
                  onClick={handleDeleteSelected}
                >
                  <Trash2 size={18} className="mr-2" />
                  Delete ({selectedItems.length})
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-outline flex items-center"
                  onClick={loadTrashItems}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[var(--color-primary)] mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} className="mr-2" />
                      Refresh
                    </>
                  )}
                </button>
                {trashItems.length > 0 && (
                  <button
                    className="btn bg-red-500 text-white hover:bg-red-600 flex items-center"
                    onClick={() => setIsConfirmingEmpty(true)}
                    disabled={loading}
                  >
                    <Trash2 size={18} className="mr-2" />
                    Empty Trash
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        
        {isConfirmingEmpty && (
          <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <AlertTriangle size={24} className="text-red-500" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">Empty Trash?</h3>
                <p className="mt-1 text-[var(--color-accent)]">
                  This will permanently delete all items in the trash. This action cannot be undone.
                </p>
                <div className="mt-3 flex space-x-2">
                  <button
                    className="btn bg-red-500 text-white hover:bg-red-600"
                    onClick={handleEmptyTrash}
                  >
                    Empty Trash
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={() => setIsConfirmingEmpty(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {loading ? (
          <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--color-primary)] mx-auto mb-4"></div>
            <p>Loading trash items...</p>
          </div>
        ) : trashItems.length === 0 ? (
          <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light p-8 text-center">
            <div className="text-[var(--color-accent)] mb-2">
              <Trash2 size={48} className="mx-auto mb-4 opacity-50" />
            </div>
            <p className="text-[var(--color-accent)] mb-2">Trash is empty</p>
            <p>Items you delete will appear here for 30 days before being permanently removed.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <TrashGridView 
            items={trashItems}
            onRestore={handleRestore}
            onDelete={handlePermanentDelete}
          />
        ) : (
          <div className="bg-white dark:bg-dark-background rounded-lg shadow-custom-light overflow-hidden mb-6">
            <div className="overflow-auto max-h-[calc(100vh-280px)]">
              <table className="min-w-full divide-y divide-[var(--color-accent)]">
                <thead className="bg-[var(--color-background)] sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-[var(--color-accent)]"
                        checked={selectedItems.length === trashItems.length && trashItems.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                      Original Location
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                      Size
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                      Deleted
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                      Days Left
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-background divide-y divide-[var(--color-accent)]">
                  {trashItems.map((item) => {
                    const daysRemaining = getDaysRemaining(item.deletedAt)
                    
                    return (
                      <tr 
                        key={item.id}
                        className={`hover:bg-[var(--color-background)] ${
                          selectedItems.includes(item.id) ? 'bg-[var(--color-background)]' : ''
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[var(--color-accent)]"
                            checked={selectedItems.includes(item.id)}
                            onChange={() => toggleItemSelection(item.id)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {item.type === 'folder' ? (
                              <Trash2 size={20} className="text-[var(--color-secondary)] mr-2" />
                            ) : (
                              <Trash2 size={20} className="text-[var(--color-primary)] mr-2" />
                            )}
                            <span className="font-medium truncate max-w-xs" title={item.name}>{item.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="truncate max-w-xs" title={item.path}>{item.path}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {item.type === 'file' && item.size !== undefined
                            ? formatFileSize(item.size)
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar size={16} className="text-[var(--color-accent)] mr-2" />
                            {item.deletedAt ? new Date(item.deletedAt).toLocaleDateString() : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Clock size={16} className={`mr-2 ${
                              daysRemaining <= 5 ? 'text-red-500' : 
                              daysRemaining <= 10 ? 'text-yellow-500' : 
                              'text-[var(--color-accent)]'
                            }`} />
                            <span className={
                              daysRemaining <= 5 ? 'text-red-500 font-medium' : 
                              daysRemaining <= 10 ? 'text-yellow-500 font-medium' : 
                              'text-[var(--color-accent)]'
                            }>
                              {daysRemaining} days
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              className="p-1 text-[#4C704C] hover:bg-[var(--color-background)] rounded"
                              onClick={() => handleRestore(item.id)}
                              title="Restore"
                            >
                              <RefreshCw size={16} />
                            </button>
                            <button
                              className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                              onClick={() => handlePermanentDelete(item.id)}
                              title="Delete permanently"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="h-12"></div> {/* Extra space at the bottom for scrolling */}
          </div>
        )}
      </div>
    </div>
  )
}

export default TrashPage